import { BehaviorSubject, EMPTY, interval } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

export default class AppBloc {
  initialized$ = new BehaviorSubject(false);
  primaryColor$ = new BehaviorSubject('#2aaae2');
  logoUrl = new BehaviorSubject(null);
  inActivityLogout$ = new BehaviorSubject(false);
  activityTimer = 0;
  uploadingSubscription = null;
  upTimeSubscription = null;

  constructor({ appRepository, authRepository, errorRepository, userRepository, drawerRepository, dialogRepository, uploadProgressRepository, freshchatService, casesRepository }) {
    this.appRepository = appRepository;
    this.authRepository = authRepository;
    this.errorRepository = errorRepository;
    this.userRepository = userRepository;
    this.drawerRepository = drawerRepository;
    this.dialogRepository = dialogRepository;
    this.uploadProgressRepository = uploadProgressRepository;
    this.casesRepository = casesRepository;
    this.freshchatService = freshchatService;
  }

  handleOpenLoading() {
    this.appRepository.handleOpenLoading();
  }

  get isAppLoading$() {
    return this.appRepository.isLoading$;
  }

  get auth$() {
    return this.authRepository.auth$;
  }

  get error$() {
    return this.errorRepository.error$;
  }

  get userDetails$() {
    return this.userRepository.userDetails$;
  }

  get notPaidCases$() {
    return this.casesRepository.notPaidCases$;
  }

  get notPaidCasesTotal$() {
    return this.casesRepository.notPaidCasesTotal$;
  }

  get requestedOrder$() {
    return this.userRepository.requestedOrder$;
  }

  get isPatientDemoAccountState() {
    return this.userRepository.getIsPatientDemoAccount();
  }

  get loadingUserDetails$() {
    return this.userRepository.loadingUserDetails$;
  }

  get userWorkspace$() {
    return this.authRepository.userWorkspace$;
  }

  async init(isTokenPath = false) {
    try {
      const token = localStorage.getItem('accessToken');

      if (!isTokenPath) {
        await this.authRepository.getIsMediphanyWorkspace();

        if (token) {
          const profile = await this.authRepository.getProfile();

          // If we got a profile but somehow lost the token during hot reload, restore it
          if (profile && !token) {
            localStorage.setItem('accessToken', token);
          }
        }
      }
    } catch (error) {
      localStorage.removeItem('accessToken');
      this.authRepository.auth$.next(null);

      // eslint-disable-next-line no-console
      console.error('Error during auth initialization:', error);
    } finally {
      this.initialized$.next(true);
    }
  }

  async getCurrentUser() {
    await this.userRepository.getCurrentUser();
  }

  startInactivityCheck() {
    if (this.uploadingSubscription) this.uploadingSubscription.unsubscribe();
    if (this.upTimeSubscription) this.upTimeSubscription.unsubscribe();

    this.uploadingSubscription = this.uploadProgressRepository.uploading$
      .pipe(
        switchMap((uploading) => {
          if (uploading) {
            return interval(1000);
          }
          return EMPTY;
        }),
        tap(() => {
          this.resetTimer();
        })
      )
      .subscribe();

    this.upTimeSubscription = interval(60000)
      .pipe(
        tap(() => {
          try {
            if (this.authRepository.checkUpTime()) {
              this.authRepository.storeLastUpTime();
            }
          } catch (ignore) {}
        })
      )
      .subscribe();

    setInterval(() => this.timerIncrement(), 60000);
  }

  resetTimer() {
    this.activityTimer = 0;
    this.inActivityLogout$.next(false);
  }

  logout() {
    this.userRepository.removeIsPatientDashboardTourShown();
    this.authRepository.logout();
    this.freshchatService.hideBubble();
  }

  timerIncrement() {
    this.activityTimer += 1;
    // if the user is logged in
    if (this.activityTimer > 15 && this.auth$.getValue()) {
      this.inActivityLogout$.next(true);
      // close any drawers or dialogs
      this.drawerRepository.toggleDrawer(false);
      this.dialogRepository.toggleDialog(false);
    }
  }
}
